// Portfolio Administration v1.0 PR 4b — TanStack Query hooks for the
// Defaults & Workout + Payment Tracking surfaces. Mirrors the PR 3/4a
// pattern: stable query-key factory + mutation hooks that invalidate the
// touched queries on success.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { portfolioAdminKeys } from "./usePortfolioAdmin";
import { loanHealthKeys } from "./useLoanHealth";
import {
  collateralRecoveriesApi,
  curePeriodsApi,
  dacaApi,
  defaultCasesApi,
  enforcerEngagementsApi,
  forbearanceApi,
  legalActionsApi,
  remediationPlansApi,
  segmentLedgerApi,
  suggestedEnforcersApi,
  workoutCostsApi,
  workoutEventsApi,
  workoutRecoveriesApi,
} from "@/api/portfolioAdmin/pr4bClient";
import type {
  ActivateDACARequest,
  AppendWorkoutEventRequest,
  CreateEnforcerEngagementRequest,
  ExecuteForbearanceRequest,
  InitiateLegalActionRequest,
  MarkCuredRequest,
  OpenDefaultCaseRequest,
  ProposeForbearanceRequest,
  ProposeRemediationPlanRequest,
  RecordWorkoutCostRequest,
  RecordWorkoutRecoveryRequest,
  SetResolutionRequest,
  TransitionDefaultCaseRequest,
} from "@/api/portfolioAdmin/pr4bTypes";

// ============================== query keys =============================

export const pr4bKeys = {
  defaultCasesForLoan: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "default-cases"] as const,
  defaultCase: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId] as const,
  stateMachine: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "state-machine"] as const,
  curePeriods: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "cure-periods"] as const,
  remediationPlans: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "remediation-plans"] as const,
  daca: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "daca"] as const,
  legalActions: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "legal-actions"] as const,
  collateralRecoveries: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "collateral-recoveries"] as const,
  workoutEvents: (caseId: string, category?: string) =>
    ["portfolio-admin", "default-case", caseId, "events", category ?? null] as const,
  forbearance: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "forbearance"] as const,
  costs: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "costs"] as const,
  recoveries: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "recoveries"] as const,
  recoveryTotals: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "recovery-totals"] as const,
  engagements: (caseId: string) =>
    ["portfolio-admin", "default-case", caseId, "engagements"] as const,
  suggestedEnforcers: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "suggested-enforcers"] as const,
  loanTape: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "loan-tape"] as const,
  loanTapeEntry: (entryId: string) =>
    ["portfolio-admin", "loan-tape", entryId] as const,
  segmentBalances: (loanId: string) =>
    ["portfolio-admin", "loan", loanId, "segments", "balance"] as const,
  segmentLedger: (loanId: string, n: number) =>
    ["portfolio-admin", "loan", loanId, "segments", n, "ledger"] as const,
};

// ============================== default cases ==========================

export function useLoanDefaultCases(loanId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.defaultCasesForLoan(loanId ?? ""),
    queryFn: () => defaultCasesApi.listForLoan(loanId!),
    enabled: !!loanId,
  });
}

export function useDefaultCase(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.defaultCase(caseId ?? ""),
    queryFn: () => defaultCasesApi.get(caseId!),
    enabled: !!caseId,
  });
}

/** Opening / curing / charging-off a case flips Loan.AccountState
 *  server-side (AccountState sync) — refresh the loan summary (header
 *  badge) and the health scorecard so the header never shows the stale
 *  pre-flip state beside the case banner. */
function invalidateLoanStateScope(qc: QueryClient, loanId: string) {
  qc.invalidateQueries({
    queryKey: portfolioAdminKeys.loanDetail(loanId),
    exact: true,
  });
  qc.invalidateQueries({ queryKey: loanHealthKeys.health(loanId) });
}

/** The DefaultCase transitions whose backend side effect flips AccountState. */
const ACCOUNT_STATE_FLIPPING_TRANSITIONS = new Set(["cured-restored", "charged-off"]);

export function useOpenDefaultCase(loanId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: OpenDefaultCaseRequest) =>
      defaultCasesApi.open(loanId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCasesForLoan(loanId) });
      invalidateLoanStateScope(qc, loanId);
    },
  });
}

export function useTransitionDefaultCase(caseId: string, loanId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TransitionDefaultCaseRequest) =>
      defaultCasesApi.transition(caseId, body),
    onSuccess: (_data, body) => {
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.stateMachine(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.workoutEvents(caseId) });
      if (loanId) {
        qc.invalidateQueries({ queryKey: pr4bKeys.defaultCasesForLoan(loanId) });
        if (ACCOUNT_STATE_FLIPPING_TRANSITIONS.has(body.toState)) {
          invalidateLoanStateScope(qc, loanId);
        }
      }
    },
  });
}

export function useSetCaseResolution(caseId: string, loanId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetResolutionRequest) =>
      defaultCasesApi.setResolution(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
      if (loanId) {
        qc.invalidateQueries({ queryKey: pr4bKeys.defaultCasesForLoan(loanId) });
      }
    },
  });
}

export function useDefaultCaseStateMachine(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.stateMachine(caseId ?? ""),
    queryFn: () => defaultCasesApi.getStateMachine(caseId!),
    enabled: !!caseId,
  });
}

// ============================== cure periods ===========================

export function useCurePeriod(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.curePeriods(caseId ?? ""),
    queryFn: () => curePeriodsApi.listForCase(caseId!),
    enabled: !!caseId,
    // The endpoint may not exist yet; treat 404/501 as "no period".
    retry: false,
  });
}

export function useMarkCurePeriodCured(caseId: string, loanId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: MarkCuredRequest }) =>
      curePeriodsApi.markCured(args.id, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.curePeriods(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
      // Curing can flip AccountState server-side.
      if (loanId) invalidateLoanStateScope(qc, loanId);
    },
  });
}

export function useExtendCurePeriod(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; additionalDays: number; justification: string }) =>
      curePeriodsApi.extend(args.id, {
        additionalDays: args.additionalDays,
        justification: args.justification,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.curePeriods(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
    },
  });
}

// ============================== remediation plans ======================

export function useRemediationPlans(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.remediationPlans(caseId ?? ""),
    queryFn: () => remediationPlansApi.listForCase(caseId!),
    enabled: !!caseId,
  });
}

export function useProposeRemediationPlan(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProposeRemediationPlanRequest) =>
      remediationPlansApi.propose(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.remediationPlans(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
    },
  });
}

// ============================== DACA ===================================

export function useDACAEvents(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.daca(caseId ?? ""),
    queryFn: () => dacaApi.listForCase(caseId!),
    enabled: !!caseId,
  });
}

export function useActivateDACA(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ActivateDACARequest) => dacaApi.activate(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.daca(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
    },
  });
}

// ============================== legal actions ==========================

export function useLegalActions(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.legalActions(caseId ?? ""),
    queryFn: () => legalActionsApi.listForCase(caseId!),
    enabled: !!caseId,
  });
}

export function useInitiateLegalAction(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InitiateLegalActionRequest) =>
      legalActionsApi.initiate(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.legalActions(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
    },
  });
}

// ============================== collateral recoveries ==================

export function useCollateralRecoveries(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.collateralRecoveries(caseId ?? ""),
    queryFn: () => collateralRecoveriesApi.listForCase(caseId!),
    enabled: !!caseId,
  });
}

// ============================== workout events =========================

export function useWorkoutEvents(
  caseId: string | undefined,
  category?: string,
) {
  return useQuery({
    queryKey: pr4bKeys.workoutEvents(caseId ?? "", category),
    queryFn: () => workoutEventsApi.listForCase(caseId!, { category }),
    enabled: !!caseId,
  });
}

export function useAppendWorkoutEvent(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AppendWorkoutEventRequest) =>
      workoutEventsApi.append(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.workoutEvents(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
    },
  });
}

// ============================== forbearance ============================

export function useForbearance(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.forbearance(caseId ?? ""),
    queryFn: () => forbearanceApi.listForCase(caseId!),
    enabled: !!caseId,
  });
}

export function useProposeForbearance(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProposeForbearanceRequest) =>
      forbearanceApi.propose(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.forbearance(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
    },
  });
}

export function useExecuteForbearance(caseId: string, loanId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; body: ExecuteForbearanceRequest }) =>
      forbearanceApi.execute(args.id, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.forbearance(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
      // Executing activates the agreement and can flip AccountState.
      if (loanId) invalidateLoanStateScope(qc, loanId);
    },
  });
}

// ============================== enforcer engagements ===================

export function useSuggestedEnforcers(loanId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.suggestedEnforcers(loanId ?? ""),
    queryFn: () => suggestedEnforcersApi.forLoan(loanId!),
    enabled: !!loanId,
  });
}

export function useEnforcerEngagementsForCase(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.engagements(caseId ?? ""),
    queryFn: () => enforcerEngagementsApi.listForCase(caseId!),
    enabled: !!caseId,
  });
}

export function useCreateEnforcerEngagement(caseId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      enforcerId: string;
      body: CreateEnforcerEngagementRequest;
    }) => enforcerEngagementsApi.createForEnforcer(args.enforcerId, args.body),
    onSuccess: () => {
      if (caseId) {
        qc.invalidateQueries({ queryKey: pr4bKeys.engagements(caseId) });
        qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
      }
    },
  });
}

// ============================== workout costs / recoveries =============

export function useWorkoutCosts(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.costs(caseId ?? ""),
    queryFn: () => workoutCostsApi.listForCase(caseId!),
    enabled: !!caseId,
  });
}

export function useRecordWorkoutCost(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordWorkoutCostRequest) =>
      workoutCostsApi.record(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.costs(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
    },
  });
}

export function useApproveWorkoutCost(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => workoutCostsApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.costs(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
    },
  });
}

export function useWorkoutRecoveries(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.recoveries(caseId ?? ""),
    queryFn: () => workoutRecoveriesApi.listForCase(caseId!),
    enabled: !!caseId,
  });
}

export function useWorkoutRecoveryTotals(caseId: string | undefined) {
  return useQuery({
    queryKey: pr4bKeys.recoveryTotals(caseId ?? ""),
    queryFn: () => workoutRecoveriesApi.totals(caseId!),
    enabled: !!caseId,
  });
}

export function useRecordWorkoutRecovery(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RecordWorkoutRecoveryRequest) =>
      workoutRecoveriesApi.record(caseId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pr4bKeys.recoveries(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.recoveryTotals(caseId) });
      qc.invalidateQueries({ queryKey: pr4bKeys.defaultCase(caseId) });
    },
  });
}

// ============================== loan tape ==============================
//
// Payment Tracking convergence: the loan tab's tape entry/lock/unlock hooks are
// gone — the tab runs on @/hooks/portfolioAdmin/useServicing (Payment System v3)
// and the legacy tape is READ-ONLY (SP-1 made the backend throw; SP-6 removed
// the dead unlock hook + its client method). The frozen tape is read via the
// servicing ledger's legacyEntries.

// SP-6 — the manual waterfall-run preview + per-loan list hooks were removed
// with the retired manual waterfall-run surface. Run history is read via usePr6
// (RunHistorySidebar).

// ============================== segment ledger =========================

export function useSegmentLedger(
  loanId: string | undefined,
  n: number,
  limit?: number,
) {
  return useQuery({
    queryKey: pr4bKeys.segmentLedger(loanId ?? "", n),
    queryFn: () => segmentLedgerApi.ledgerForSegment(loanId!, n, { limit }),
    enabled: !!loanId,
  });
}
